import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import AdminHeader from "@/components/admin/AdminHeader";
import {
  fetchLeads,
  fetchImpact,
  markLeadContacted,
  updateImpact,
} from "@/lib/api";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "meeting", label: "Meeting Requests" },
  { key: "bulk", label: "Bulk Enquiries" },
];

function formatDate(iso) {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toCSV(leads) {
  const cols = ["created_at", "lead_type", "source", "name", "email", "phone", "company", "quantity", "message", "contacted", "contacted_at", "id"];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = cols.join(",");
  const rows = leads.map((l) => cols.map((c) => esc(l[c])).join(","));
  return [header, ...rows].join("\n");
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [filter, setFilter] = useState("all");
  const [impact, setImpact] = useState({ goal: 30000, planted: 5400, year: 2026 });
  const [savingImpact, setSavingImpact] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const loadAll = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const [rows, impactStats] = await Promise.all([
        fetchLeads({ limit: 500 }),
        fetchImpact(),
      ]);
      setLeads(rows);
      setImpact(impactStats);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoadingLeads(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/admin/login", { replace: true });
      return;
    }
    if (!user.is_admin) {
      navigate("/admin/login", { replace: true });
      return;
    }
    loadAll();
  }, [user, loading, navigate, loadAll]);

  const filteredLeads = useMemo(() => {
    if (filter === "all") return leads;
    return leads.filter((l) => l.lead_type === filter);
  }, [leads, filter]);

  const stats = useMemo(() => ({
    total: leads.length,
    meeting: leads.filter((l) => l.lead_type === "meeting").length,
    bulk: leads.filter((l) => l.lead_type === "bulk").length,
    contacted: leads.filter((l) => l.contacted).length,
  }), [leads]);

  const handleMarkContacted = async (id) => {
    try {
      const updated = await markLeadContacted(id);
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      toast.success("Marked as contacted.");
    } catch {
      toast.error("Could not update lead.");
    }
  };

  const handleImpactSave = async (patch) => {
    setSavingImpact(true);
    try {
      const next = await updateImpact(patch);
      setImpact(next);
      toast.success("Counter updated.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save.";
      toast.error(typeof msg === "string" ? msg : "Could not save.");
    } finally {
      setSavingImpact(false);
    }
  };

  const handleExport = () => {
    const csv = toCSV(filteredLeads);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `utsah-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !user) {
    return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;
  }

  return (
    <div className="admin-shell utsah-app" data-testid="admin-dashboard">
      <AdminHeader active="leads" onLogout={async () => { await logout(); navigate("/admin/login"); }} email={user.email} />

      <div className="admin-container">
        <div className="admin-heading-row">
          <div>
            <div className="eyebrow">Dashboard</div>
            <h1 className="admin-heading">Leads &amp; <em>Impact.</em></h1>
          </div>
          <button className="admin-export-btn" onClick={handleExport} data-testid="admin-export-csv">
            Export CSV
          </button>
        </div>

        {/* Impact editor */}
        <ImpactEditor
          impact={impact}
          saving={savingImpact}
          onSave={handleImpactSave}
        />

        {/* Stats strip */}
        <div className="admin-stats">
          <StatCard label="Total leads" value={stats.total} testid="stat-total" />
          <StatCard label="Meeting requests" value={stats.meeting} testid="stat-meeting" />
          <StatCard label="Bulk enquiries" value={stats.bulk} testid="stat-bulk" />
          <StatCard label="Marked contacted" value={stats.contacted} testid="stat-contacted" />
        </div>

        {/* Filters */}
        <div className="admin-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`admin-filter ${filter === f.key ? "active" : ""}`}
              onClick={() => setFilter(f.key)}
              data-testid={`admin-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Leads table */}
        <div className="admin-table-wrap" data-testid="admin-leads-table">
          {loadingLeads ? (
            <div className="admin-empty">Loading leads…</div>
          ) : filteredLeads.length === 0 ? (
            <div className="admin-empty">No leads yet.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Captured</th>
                  <th style={{ width: 90 }}>Type</th>
                  <th style={{ width: 130 }}>Source</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th style={{ width: 150 }}>Phone</th>
                  <th>Company</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 130 }} />
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l) => {
                  const isOpen = expanded === l.id;
                  return (
                    <LeadRow
                      key={l.id}
                      lead={l}
                      isOpen={isOpen}
                      onToggle={() => setExpanded((cur) => (cur === l.id ? null : l.id))}
                      onMarkContacted={() => handleMarkContacted(l.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}

function ImpactEditor({ impact, saving, onSave }) {
  const [planted, setPlanted] = useState(impact.planted);
  const [goal, setGoal] = useState(impact.goal);

  useEffect(() => { setPlanted(impact.planted); setGoal(impact.goal); }, [impact.planted, impact.goal]);

  const pct = Math.min(100, Math.round((impact.planted / (impact.goal || 1)) * 100));

  return (
    <section className="admin-impact-card" data-testid="admin-impact-card">
      <div>
        <div className="eyebrow">Live Counter · Impact</div>
        <div className="admin-impact-numbers">
          <span className="admin-impact-planted" data-testid="admin-impact-planted-display">{impact.planted.toLocaleString()}</span>
          <span className="admin-impact-slash">/</span>
          <span className="admin-impact-goal" data-testid="admin-impact-goal-display">{impact.goal.toLocaleString()}</span>
        </div>
        <div className="admin-progress"><div className="admin-progress-fill" style={{ width: `${pct}%` }} /></div>
        <div className="admin-progress-caption">{pct}% of {impact.year} goal</div>
      </div>
      <form
        className="admin-impact-form"
        onSubmit={(e) => {
          e.preventDefault();
          const patch = {};
          const p = Number(planted); const g = Number(goal);
          if (Number.isFinite(p) && p !== impact.planted) patch.planted = p;
          if (Number.isFinite(g) && g !== impact.goal) patch.goal = g;
          if (Object.keys(patch).length === 0) return;
          onSave(patch);
        }}
      >
        <label>
          <span>Trees planted</span>
          <input
            type="number"
            min={0}
            value={planted}
            onChange={(e) => setPlanted(e.target.value)}
            className="lead-input"
            data-testid="admin-impact-planted-input"
          />
        </label>
        <label>
          <span>Goal</span>
          <input
            type="number"
            min={0}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className="lead-input"
            data-testid="admin-impact-goal-input"
          />
        </label>
        <button
          type="submit"
          className="lead-submit"
          disabled={saving}
          data-testid="admin-impact-save"
          style={{ marginTop: 0 }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </form>
    </section>
  );
}

function LeadRow({ lead: l, isOpen, onToggle, onMarkContacted }) {
  return (
    <>
      <tr
        className={`admin-row ${isOpen ? "open" : ""}`.trim()}
        onClick={onToggle}
        data-testid={`admin-lead-row-${l.id}`}
      >
        <td className="col-date">{formatDate(l.created_at)}</td>
        <td>
          <span className={`type-chip type-${l.lead_type}`}>
            {l.lead_type === "meeting" ? "Meeting" : "Bulk"}
          </span>
        </td>
        <td className="col-source">{l.source || "—"}</td>
        <td className="col-name">{l.name}</td>
        <td className="col-email">{l.email}</td>
        <td className="col-phone">{l.phone || "—"}</td>
        <td className="col-company">{l.company || "—"}</td>
        <td>
          {l.contacted ? (
            <span className="status-chip contacted">Contacted</span>
          ) : (
            <span className="status-chip new">New</span>
          )}
        </td>
        <td>
          {!l.contacted && (
            <button
              className="admin-row-btn"
              onClick={(e) => { e.stopPropagation(); onMarkContacted(); }}
              data-testid={`mark-contacted-${l.id}`}
            >
              Mark contacted
            </button>
          )}
        </td>
      </tr>
      {isOpen && (
        <tr className="admin-row-details">
          <td colSpan={9}>
            <div className="admin-details-grid">
              {l.quantity && <DetailItem label="Quantity" value={l.quantity} />}
              {l.contacted_at && <DetailItem label="Contacted on" value={formatDate(l.contacted_at)} />}
              <DetailItem label="Lead ID" value={l.id} mono />
              {l.message && (
                <div className="admin-detail-message">
                  <div className="admin-detail-label">Message</div>
                  <div className="admin-detail-body">{l.message}</div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="admin-stat" data-testid={testid}>
      <div className="admin-stat-value">{value}</div>
      <div className="admin-stat-label">{label}</div>
    </div>
  );
}

function DetailItem({ label, value, mono }) {
  return (
    <div className="admin-detail-item">
      <div className="admin-detail-label">{label}</div>
      <div className={`admin-detail-body ${mono ? "mono" : ""}`.trim()}>{value}</div>
    </div>
  );
}
